import Dialog from 'components/Dialog';
import React from 'react';
import {Grid} from '@mui/material';
import FormikTextField from 'components/inputs/FormikTextField';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import * as yup from 'common/yup';
import {usePostApi} from 'common/hooks/usePostApi';
import {INav} from 'features/layout/redux/helpers/FoldersHelper';
import {useDispatch, useSelector} from 'react-redux';
import {layoutActions} from 'features/layout/redux';
import {packId, unpackId} from 'common/diplomatIdUtil';
import {useTranslation} from 'react-i18next';
import selectors from 'features/layout/redux/selectors';
import {IDiplomatObject} from 'types/DiplomatObject';
import history from 'redux/history';
import {ITransaction} from 'types/diplomat/transaction/Transaction';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import {transactionSynchronizationGetters} from 'redux/transactions/transactionSynchronizationGetters';
import {synchronizationsSelectors} from 'redux/synchronizations';
import {action} from 'redux/helpers';
import {pageTypes} from 'features/layout/CommonPage/redux';
import Formik from 'components/hook-form/Formik';

const validationSchema = yup.object({
   name: yup.string().required(),
});

type Props = {
   data?: INav;
   onClose: () => void;
};

const RenameTransactionDialog = ({data, onClose}: Props) => {
   const {t} = useTranslation();
   const dispatch = useDispatch();
   const transactions = useSelector(selectors.transactionsSelector);
   const synchronizations = useSelector(synchronizationsSelectors.synchronizationsSelector);

   const {id} = unpackId(data?.id);

   const isSync = data?.objectType === DiplomatObjectType.enum.SYNCHRONIZATION;
   const {label, baseApi, nameField} = isSync
      ? transactionSynchronizationGetters.Synchronization
      : transactionSynchronizationGetters.Transaction;

   const {request} = usePostApi(`${baseApi}/${id}/rename`, {
      onSuccess: (data: ITransaction) => {
         dispatch(layoutActions.loadTreeNav());
         dispatch(action(pageTypes.GET_DATA.SUCCESS, data));
         onClose();
         history.push(`/${packId(data.objectType, data.id)}`);
      },
      onFailure: onClose,
   });

   const validateName = (value: string) => {
      const isNameExists = (isSync ? synchronizations : transactions).some(
         (obj: IDiplomatObject) => obj.name === value
      );
      if (isNameExists) {
         return t(`Diplomat already contains a {{label}} named {{name}}`, {
            label,
            name: value,
         });
      }
   };

   return (
      <Dialog maxWidth="sm" header={t('Rename {{label}}', {label})} close={onClose}>
         <Formik
            initialValues={validationSchema.cast({name: data?.name})}
            onSubmit={(values) => {
               request(null, {
                  searchParams: {
                     [nameField]: values.name,
                  },
               });
            }}
         >
            <>
               <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={8}>
                     <FormikTextField
                        autoFocus
                        type="text"
                        name="name"
                        label={t('New {{label}} Name:', {label})}
                        required
                        validate={validateName}
                     />
                  </Grid>
               </Grid>
               <Grid item xs={12}>
                  <FormFooterButtons cancel={onClose} />
               </Grid>
            </>
         </Formik>
      </Dialog>
   );
};

export default RenameTransactionDialog;
